import {
  BankOutlined,
  CalendarOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HomeOutlined,
  IdcardOutlined,
  NumberOutlined,
  PhoneOutlined,
  RightOutlined,
  SafetyOutlined,
  SearchOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
// PhoneOutlined used in modal rows
import { DatePicker, Input, Select } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { CREATE_jwel1 } from "../../Config/Config";

/* ─── palette ──────────────────────────────────────────────────── */
const C = {
  pri: "#4f46e5",
  priLight: "#eef2ff",
  priMid: "#c7d2fe",
  border: "#e5e7eb",
  borderFocus: "#a5b4fc",
  textPri: "#1e1b4b",
  textMuted: "#6b7280",
  surface: "#ffffff",
  sectionBg: "#f5f3ff",
  sectionBorder: "#ede9fe",
  dark: "#1e2d40",
  okLight: "#d1fae5",
  okBorder: "#6ee7b7",
  goldLight: "#fef3c7",
  goldBorder: "#fde68a",
};

/* KV card themes  [bg, border, labelColor, valueColor] */
const T = {
  grey: ["#f8fafc", "#e2e8f0", "#94a3b8", "#0f172a"],
  amber: ["#fffbeb", "#fde68a", "#d97706", "#92400e"],
  blue: ["#eff6ff", "#bfdbfe", "#3b82f6", "#1d4ed8"],
  teal: ["#f0fdfa", "#99f6e4", "#0d9488", "#134e4a"],
};

/* ─── avatar helpers ────────────────────────────────────────────── */
const AV_COLORS = [
  "#e74c3c",
  "#e67e22",
  "#2ecc71",
  "#1abc9c",
  "#3498db",
  "#9b59b6",
  "#e91e63",
  "#00bcd4",
  "#8bc34a",
  "#ff5722",
  "#607d8b",
  "#f39c12",
];
const avColor = (n = "") =>
  AV_COLORS[(n.charCodeAt(0) || 0) % AV_COLORS.length];
const avInit = (n = "") =>
  n
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
const clean = (v) => {
  const s = String(v ?? "").trim();
  return s === "-" ? "" : s;
};

export const financialYearCode = (d = new Date()) => {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return String(y).slice(-2);
};

/* ─── CSS ───────────────────────────────────────────────────────── */
const CSS = `
  .phf-root {
    background:${C.surface};
    border-bottom:2px solid ${C.sectionBorder};
    font-family:'Inter',system-ui,sans-serif;
    flex-shrink:0;
  }
  .phf-body {
    display:grid;
    grid-template-columns:1fr 1.35fr;
    gap:10px;
    padding:8px 14px 10px;
    align-items:stretch;        /* equal height both sides */
  }
  @media(max-width:960px){.phf-body{grid-template-columns:1fr;}}

  /* ── section wrapper ── */
  .phf-sec {
    background:${C.sectionBg};
    border:1.5px solid ${C.sectionBorder};
    border-radius:10px;
    padding:8px 12px 10px;
    min-width:0;
    display:flex; flex-direction:column;
  }
  .phf-sec-title {
    font-size:9px; font-weight:800; text-transform:uppercase;
    letter-spacing:1px; color:${C.pri};
    margin-bottom:7px;
    display:flex; align-items:center; gap:5px;
    flex-shrink:0;
  }

  /* ══ ENTRY DETAILS — pill-card grid ══ */
  .phf-egrid {
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:6px;
    flex:1;
    align-content:start;
  }
  @media(max-width:1280px){.phf-egrid{grid-template-columns:repeat(2,minmax(0,1fr));}}

  /* each entry field is a pill card — label on top, control below */
  .phf-ef {
    background:#fff;
    border:1.5px solid ${C.border};
    border-radius:8px;
    padding:5px 9px 6px;
    display:flex; flex-direction:column; gap:3px;
    min-width:0;
    transition:border-color .14s;
  }
  .phf-ef:focus-within {
    border-color:${C.pri};
    box-shadow:0 0 0 2px rgba(79,70,229,.1);
  }
  .phf-ef-lbl {
    font-size:9px; font-weight:700; color:#94a3b8;
    text-transform:uppercase; letter-spacing:.6px;
    display:flex; align-items:center; gap:3px; white-space:nowrap;
    line-height:1;
  }
  .phf-ef-req { color:#e11d48; }

  /* strip all ant borders/bg — the card itself is the container */
  .phf-ef .ant-input,
  .phf-ef .ant-picker,
  .phf-ef .ant-select-selector {
    border:none !important; box-shadow:none !important;
    background:transparent !important; padding:0 !important;
    font-size:12.5px !important; font-weight:700 !important;
    color:${C.textPri} !important; height:auto !important;
    line-height:1.3 !important;
  }
  .phf-ef .ant-picker { width:100% !important; }
  .phf-ef .ant-picker-input input { font-size:12.5px !important; font-weight:700 !important; color:${C.textPri} !important; }
  .phf-ef .ant-select-selector { min-height:unset !important; }
  .phf-ef .ant-select-selection-item { font-size:12.5px !important; font-weight:700 !important; color:${C.textPri} !important; line-height:1.4 !important; }
  .phf-ef .ant-select-selection-placeholder { font-size:12px !important; line-height:1.4 !important; }
  .phf-ef .ant-picker-suffix { color:#9ca3af; font-size:11px; }
  .phf-ef .ant-select-arrow { color:#9ca3af; font-size:10px; }

  /* entry-no pill (read-only) */
  .phf-eno {
    font-size:13px; font-weight:800; color:${C.pri};
    letter-spacing:.3px; line-height:1.3;
    cursor:default; user-select:none;
  }
  .phf-eno.empty { color:#9ca3af; font-weight:500; font-size:12px; }

  .phf-ptype-err {
    font-size:11px; font-weight:600; color:#e11d48; line-height:1.3;
  }
  .phf-dd .ant-select-item { font-size:12.5px !important; padding:6px 11px !important; }
  .phf-dd .ant-select-item-option-selected {
    background:${C.priLight} !important; color:${C.pri} !important; font-weight:700 !important;
  }
  .phf-dd .ant-select-item-option-active { background:#f5f3ff !important; }

  /* ══ SUPPLIER PANEL ══ */

  /* ── selected: single flat row ── */
  .phf-sp-row {
    display:flex;
    align-items:stretch;
    gap:7px;
    min-width:0;
    flex:1;
  }

  /* avatar + name block */
  .phf-sav-wrap {
    flex-shrink:0;
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    background:${C.dark}; border-radius:9px;
    padding:6px 8px; gap:3px; min-width:52px;
  }
  .phf-sav {
    width:30px; height:30px; border-radius:7px;
    display:flex; align-items:center; justify-content:center;
    font-size:12px; font-weight:800; color:#fff; user-select:none;
  }
  .phf-sav-name {
    font-size:8.5px; font-weight:700; color:#94a3b8;
    text-align:center; max-width:68px;
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
    text-transform:uppercase; letter-spacing:.3px;
  }

  /* kv inline strip — two rows */
  .phf-kv-strip {
    flex:1; min-width:0;
    display:flex;
    flex-direction:column;
    gap:5px;
  }
  .phf-kv-row1 {
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:5px;
  }
  .phf-kv-row2 { display:flex; }
  .phf-kv-row2 .phf-kv { flex:1; }
  @media(max-width:1200px){.phf-kv-row1{grid-template-columns:repeat(2,minmax(0,1fr));}}

  .phf-kv {
    border-radius:7px; border:1.5px solid;
    padding:5px 8px 6px; min-width:0;
    display:flex; flex-direction:column; justify-content:center;
  }
  .phf-kv-lbl {
    font-size:9px; font-weight:700; text-transform:uppercase;
    letter-spacing:.5px; display:flex; align-items:center; gap:3px;
    margin-bottom:2px; line-height:1;
  }
  .phf-kv-val {
    font-size:12px; font-weight:700;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; line-height:1.3;
  }
  .phf-kv-val.wrap { white-space:normal; font-size:11px; line-height:1.4; }
  .phf-kv-val.em   { font-weight:400; font-style:italic; opacity:.4; }

  /* station pill */
  .phf-st {
    display:inline-flex; align-items:center; gap:3px;
    padding:1px 8px; border-radius:20px; font-size:10.5px; font-weight:700;
    border:1.5px solid ${C.border}; background:#f3f4f6; color:#6b7280;
    width:fit-content;
  }
  .phf-st.local      { background:${C.okLight}; border-color:${C.okBorder}; color:#065f46; }
  .phf-st.outstation { background:${C.goldLight}; border-color:${C.goldBorder}; color:#92400e; }

  /* action buttons */
  .phf-actions-col {
    display:flex; flex-direction:column; justify-content:center; gap:5px; flex-shrink:0;
  }
  .phf-ib {
    width:28px; height:28px; border-radius:7px;
    display:flex; align-items:center; justify-content:center;
    border:none; cursor:pointer; font-size:12px;
    transition:opacity .14s,transform .12s;
  }
  .phf-ib:hover:not(:disabled){ opacity:.82; transform:scale(1.07); }
  .phf-ib:disabled{ opacity:.4; cursor:not-allowed; transform:none; }
  .phf-ib.ch{ background:#4f46e5; color:#fff; }
  .phf-ib.cl{ background:#fee2e2; color:#dc2626; }

  /* empty state — flex centered, fills the same height */
  .phf-sempty {
    flex:1;
    display:flex; align-items:center; justify-content:center;
    gap:10px;
  }
  .phf-sempty-icon {
    width:34px; height:34px; border-radius:50%;
    background:${C.priLight}; display:flex; align-items:center;
    justify-content:center; font-size:16px; color:${C.pri}; flex-shrink:0;
  }
  .phf-sempty-txt { font-size:12px; color:${C.textMuted}; font-weight:500; }
  .phf-browsebtn {
    display:inline-flex; align-items:center; gap:6px;
    height:28px; padding:0 12px; border-radius:7px;
    border:1.5px solid ${C.priMid}; background:${C.priLight};
    color:${C.pri}; font-size:11.5px; font-weight:700;
    cursor:pointer; transition:background .14s; flex-shrink:0;
  }
  .phf-browsebtn:hover:not(:disabled){ background:${C.priMid}; }
  .phf-browsebtn:disabled{ opacity:.5; cursor:not-allowed; }

  /* ══ MODAL ══ */
  .phf-ov {
    position:fixed; inset:0; z-index:1050;
    background:rgba(15,23,42,.48);
    display:flex; align-items:center; justify-content:center;
  }
  .phf-mbox {
    background:#fff; border-radius:16px;
    width:840px; max-width:95vw; max-height:86vh;
    display:flex; flex-direction:column;
    box-shadow:0 24px 56px rgba(0,0,0,.24); overflow:hidden;
  }
  .phf-mhdr {
    background:${C.dark}; padding:18px 22px 16px;
    display:flex; align-items:center; gap:14px; flex-shrink:0;
  }
  .phf-mhdr-ico {
    width:46px; height:46px; border-radius:12px;
    background:rgba(255,255,255,.12);
    display:flex; align-items:center; justify-content:center;
    font-size:21px; color:#fff; flex-shrink:0;
  }
  .phf-mhdr-t  { flex:1; min-width:0; }
  .phf-mhdr-h  { font-size:16px; font-weight:800; color:#fff; line-height:1.2; }
  .phf-mhdr-s  { font-size:11.5px; color:#94a3b8; margin-top:2px; }
  .phf-mhdr-cl {
    width:34px; height:34px; border-radius:8px;
    background:rgba(255,255,255,.1); border:none;
    display:flex; align-items:center; justify-content:center;
    color:#94a3b8; font-size:14px; cursor:pointer; transition:background .14s;
  }
  .phf-mhdr-cl:hover{ background:rgba(255,255,255,.2); color:#fff; }

  .phf-mflt {
    display:flex; gap:10px; padding:12px 18px;
    border-bottom:1px solid #f1f5f9; flex-shrink:0;
  }
  .phf-mflt .ant-input-affix-wrapper {
    border-radius:8px !important; font-size:12.5px !important;
    border-color:#e2e8f0 !important; background:#f8fafc !important;
  }
  .phf-mflt .ant-input-affix-wrapper:focus-within {
    border-color:${C.pri} !important;
    box-shadow:0 0 0 2px rgba(79,70,229,.12) !important;
    background:#fff !important;
  }
  .phf-mflt .ant-select-selector {
    border-radius:8px !important; font-size:12.5px !important;
    border-color:#e2e8f0 !important; background:#f8fafc !important;
    height:34px !important; display:flex !important; align-items:center !important;
  }

  .phf-mlist { flex:1; overflow-y:auto; }
  .phf-mlist::-webkit-scrollbar{ width:4px; }
  .phf-mlist::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:4px; }

  .phf-mrow {
    display:flex; align-items:center; gap:12px;
    padding:11px 20px; border-bottom:1px solid #f8fafc;
    cursor:pointer; transition:background .1s;
  }
  .phf-mrow:hover{ background:#f8fafc; }
  .phf-mrow:last-child{ border-bottom:none; }
  .phf-mrav {
    flex-shrink:0; width:42px; height:42px; border-radius:10px;
    display:flex; align-items:center; justify-content:center;
    font-size:15px; font-weight:800; color:#fff; user-select:none;
  }
  .phf-mri { flex:1; min-width:0; }
  .phf-mrn {
    font-size:13.5px; font-weight:700; color:#0f172a;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; line-height:1.3;
  }
  .phf-mrs {
    font-size:11px; color:#64748b;
    display:flex; align-items:center; gap:4px; margin-top:2px;
  }
  .phf-mrph {
    display:flex; align-items:center; gap:5px;
    font-size:12.5px; font-weight:600; color:#334155; min-width:120px;
  }
  .phf-mrph svg{ color:#94a3b8; font-size:11px; }
  .phf-mrst {
    min-width:82px; text-align:center;
    font-size:11px; font-weight:700; padding:3px 11px; border-radius:20px;
    border:1.5px solid #e2e8f0; background:#f1f5f9; color:#64748b;
  }
  .phf-mrst.local      { background:${C.okLight}; border-color:${C.okBorder}; color:#065f46; }
  .phf-mrst.outstation { background:${C.goldLight}; border-color:${C.goldBorder}; color:#92400e; }
  .phf-mrarr {
    width:28px; height:28px; border-radius:50%;
    display:flex; align-items:center; justify-content:center;
    background:#f1f5f9; color:#94a3b8; font-size:11px; flex-shrink:0;
    transition:background .11s,color .11s;
  }
  .phf-mrow:hover .phf-mrarr{ background:${C.priLight}; color:${C.pri}; }
  .phf-mempty {
    display:flex; flex-direction:column; align-items:center;
    justify-content:center; padding:44px 20px;
    color:#94a3b8; gap:8px; font-size:13.5px;
  }
  .phf-mft {
    display:flex; align-items:center; justify-content:space-between;
    padding:10px 20px; border-top:1px solid #f1f5f9; flex-shrink:0;
  }
  .phf-mft-hint{ font-size:11.5px; color:#94a3b8; }
  .phf-mft-cl {
    display:inline-flex; align-items:center; gap:6px;
    height:32px; padding:0 16px; border-radius:8px;
    border:1.5px solid #e2e8f0; background:#fff;
    color:#334155; font-size:12.5px; font-weight:600;
    cursor:pointer; transition:background .14s;
  }
  .phf-mft-cl:hover{ background:#f8fafc; }
`;

/* ─── KV card ───────────────────────────────────────────────────── */
const KV = ({
  label,
  icon,
  value: val,
  t = "grey",
  wrap = false,
  className = "",
}) => {
  const [bg, br, lc, vc] = T[t] || T.grey;
  return (
    <div
      className={`phf-kv ${className}`}
      style={{ background: bg, borderColor: br }}
    >
      <div className="phf-kv-lbl" style={{ color: lc }}>
        {icon}
        {label}
      </div>
      <div
        className={`phf-kv-val${wrap ? " wrap" : ""}${!val ? " em" : ""}`}
        style={{ color: val ? vc : undefined }}
      >
        {val || "—"}
      </div>
    </div>
  );
};

/* ─── Entry field ───────────────────────────────────────────────── */
const EF = ({ label, icon, required, children, nav }) => (
  <div className="phf-ef" onKeyDownCapture={nav}>
    <span className="phf-ef-lbl">
      {icon}
      {label}
      {required && <span className="phf-ef-req"> *</span>}
    </span>
    {children}
  </div>
);

/* ─── Supplier Browse Modal ─────────────────────────────────────── */
const SupplierModal = ({ open, onClose, suppliers, loading, onSelect }) => {
  const [nameQ, setNameQ] = useState("");
  const [areaQ, setAreaQ] = useState("");

  const areas = useMemo(() => {
    const s = new Set();
    suppliers.forEach((x) => {
      if (x.area) s.add(x.area.trim());
    });
    return [...s].sort();
  }, [suppliers]);

  const rows = useMemo(
    () =>
      suppliers.filter((s) => {
        const nm = nameQ.trim().toLowerCase();
        const ar = areaQ.trim().toLowerCase();
        return (
          (!nm ||
            (s.Custname || "").toLowerCase().includes(nm) ||
            (s.SName || "").toLowerCase().includes(nm)) &&
          (!ar || (s.area || "").toLowerCase() === ar)
        );
      }),
    [suppliers, nameQ, areaQ],
  );

  if (!open) return null;

  return (
    <div
      className="phf-ov"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style>{CSS}</style>
      <div className="phf-mbox">
        {/* header */}
        <div className="phf-mhdr">
          <div className="phf-mhdr-ico">
            <ShopOutlined />
          </div>
          <div className="phf-mhdr-t">
            <div className="phf-mhdr-h">Select Supplier</div>
            <div className="phf-mhdr-s">
              {loading
                ? "Loading…"
                : `${rows.length} of ${suppliers.length} suppliers`}
            </div>
          </div>
          <button className="phf-mhdr-cl" onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>

        {/* filters */}
        <div className="phf-mflt">
          <Input
            prefix={<UserOutlined style={{ color: "#9ca3af", fontSize: 12 }} />}
            placeholder="Filter by name…"
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            allowClear
            autoFocus
            style={{ flex: 2 }}
          />
          <Select
            placeholder="All Areas"
            value={areaQ || undefined}
            allowClear
            showSearch
            onChange={(v) => setAreaQ(v || "")}
            style={{ flex: 1, minWidth: 130 }}
            options={areas.map((a) => ({ value: a, label: a }))}
            suffixIcon={<EnvironmentOutlined style={{ color: "#9ca3af" }} />}
          />
        </div>

        {/* list */}
        <div className="phf-mlist">
          {loading ? (
            <div className="phf-mempty">Loading suppliers…</div>
          ) : rows.length === 0 ? (
            <div className="phf-mempty">
              <ShopOutlined style={{ fontSize: 30, opacity: 0.25 }} />
              No suppliers found
            </div>
          ) : (
            rows.map((s, i) => {
              const sc =
                String(s.Station || "").toUpperCase() === "OUTSTATION"
                  ? "outstation"
                  : String(s.Station || "").toUpperCase() === "LOCAL"
                    ? "local"
                    : "";
              const ph = clean(s.Mobileno) || clean(s.Phoneno);
              return (
                <div
                  key={`${s.Custname}||${s.area}||${i}`}
                  className="phf-mrow"
                  onClick={() => {
                    onSelect(s);
                    onClose();
                  }}
                >
                  <div
                    className="phf-mrav"
                    style={{ background: avColor(s.Custname) }}
                  >
                    {avInit(s.Custname)}
                  </div>
                  <div className="phf-mri">
                    <div className="phf-mrn">{s.Custname}</div>
                    {s.area && (
                      <div className="phf-mrs">
                        <EnvironmentOutlined style={{ fontSize: 10 }} />
                        {s.area}
                      </div>
                    )}
                  </div>
                  {ph && (
                    <div className="phf-mrph">
                      <PhoneOutlined />
                      {ph}
                    </div>
                  )}
                  <span className={`phf-mrst ${sc}`}>
                    {sc === "outstation"
                      ? "Outstation"
                      : sc === "local"
                        ? "Local"
                        : "—"}
                  </span>
                  <div className="phf-mrarr">
                    <RightOutlined />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* footer */}
        <div className="phf-mft">
          <span className="phf-mft-hint">
            Click any row to select · {areas.length} area
            {areas.length !== 1 ? "s" : ""} available
          </span>
          <button className="phf-mft-cl" onClick={onClose}>
            <CloseOutlined /> Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Focus order ───────────────────────────────────────────────── */
const FOCUS = ["ptype", "invoiceNo", "invoiceDate"];

/* ════════════════════════════════════════════════════════════════
   PurchaseHeaderForm
════════════════════════════════════════════════════════════════ */
const PurchaseHeaderForm = ({
  value = {},
  onChange,
  disabled = false,
  onFinish,
  focusTrigger,
}) => {
  const tenantName = localStorage.getItem("tenantName");

  const refs = useRef({});
  const opens = useRef({});
  const setRef = (k) => (el) => {
    refs.current[k] = el;
  };
  const trkOp = (k) => (o) => {
    opens.current[k] = o;
  };

  const goNext = (k) => {
    const nxt = FOCUS[FOCUS.indexOf(k) + 1];
    if (!nxt) {
      onFinish?.();
      return;
    }
    refs.current[nxt]?.focus?.();
  };

  const vref = useRef(value);
  vref.current = value;
  const [warn, setWarn] = useState({});
  const [browseOpen, setBrowseOpen] = useState(false);

  const miss = (k) => {
    const v = vref.current;
    if (k === "ptype") return !v.purchaseType;
    if (k === "invoiceNo") return !String(v.invoiceNo || "").trim();
    if (k === "invoiceDate") return !v.invoiceDate;
    return false;
  };
  const err = (k) => (warn[k] && miss(k) ? "error" : undefined);
  const flag = (k) => setWarn((w) => ({ ...w, [k]: true }));

  const nav = (k, kind) => (e) => {
    if (e.key !== "Enter") return;
    if (kind === "select") {
      if (opens.current[k]) return;
      if (miss(k)) {
        flag(k);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      goNext(k);
    } else if (kind === "date") {
      setTimeout(() => (miss(k) ? flag(k) : goNext(k)), 50);
    } else {
      e.preventDefault();
      if (miss(k)) {
        flag(k);
        return;
      }
      goNext(k);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => refs.current.ptype?.focus?.(), 50);
    return () => clearTimeout(t);
  }, []);

  const [types, setTypes] = useState([]);
  const [tLoad, setTLoad] = useState(true);
  const [tErr, setTErr] = useState(null);
  const [sups, setSups] = useState([]);
  const [sLoad, setSLoad] = useState(false);
  const [ePfx, setEPfx] = useState(null);
  const [eCnt, setECnt] = useState(null);

  const patch = (p) => onChange?.({ ...value, ...p });

  /* auto-select default purchase type if null or unselected */
  useEffect(() => {
    if (!value.purchaseType && types.length > 0) {
      patch({ purchaseType: types[0] });
    }
  }, [types, value.purchaseType]);

  /* focus purchase type on trigger */
  useEffect(() => {
    if (focusTrigger) {
      const t = setTimeout(() => refs.current.ptype?.focus?.(), 100);
      return () => clearTimeout(t);
    }
  }, [focusTrigger]);

  /* fetch purchase types */
  useEffect(() => {
    let c = false;
    setTLoad(true);
    setTErr(null);
    axios
      .get(`${CREATE_jwel1}/api/Master/GetDataFromGivenTableName`, {
        params: { tableName: "purchase_type" },
        headers: { tenantName },
      })
      .then(({ data }) => {
        if (c) return;
        const list = Array.isArray(data) ? data : [];
        setTypes(list);
        if (!value.purchaseType && list.length > 0)
          onChange?.({ ...value, purchaseType: list[0] });
      })
      .catch((e) => {
        if (!c) setTErr(e?.response?.data?.message || e.message || "Failed");
      })
      .finally(() => {
        if (!c) setTLoad(false);
      });
    return () => {
      c = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantName]);

  /* fetch suppliers */
  useEffect(() => {
    let c = false;
    setSLoad(true);
    axios
      .get(`${CREATE_jwel1}/api/Master/GetDataFromGivenTableName`, {
        params: { tableName: "SUPPLIER" },
        headers: { tenantName },
      })
      .then(({ data }) => {
        if (!c && Array.isArray(data)) setSups(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!c) setSLoad(false);
      });
    return () => {
      c = true;
    };
  }, [tenantName]);

  /* entry no ingredients */
  useEffect(() => {
    let c = false;
    const get = (t) =>
      axios.get(`${CREATE_jwel1}/api/Master/GetDataFromGivenTableName`, {
        params: { tableName: t },
        headers: { tenantName },
      });
    get("counter_config")
      .then(({ data }) => {
        if (c) return;
        const row = Array.isArray(data) ? data[0] : null;
        const p = Number(row?.ENTRYNO_PREFIX);
        setEPfx(Number.isFinite(p) ? String(Math.trunc(p)) : "");
      })
      .catch(() => {
        if (!c) setEPfx("");
      });
    get("PURCHINVOICE_MAST")
      .then(({ data }) => {
        if (!c) setECnt(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {
        if (!c) setECnt(null);
      });
    return () => {
      c = true;
    };
  }, [tenantName]);

  /* build entry no */
  useEffect(() => {
    if (!value.purchaseType?.PNO || ePfx === null || eCnt === null) return;
    const en = `${value.purchaseType.PNO}${ePfx}${financialYearCode()}${eCnt + 1}`;
    if (en !== value.entryNo) onChange?.({ ...value, entryNo: en });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.purchaseType?.PNO, ePfx, eCnt]);

  const cleared = {
    supplierObj: null,
    shortName: "",
    address: "",
    gstin: "",
    panNo: "",
    station: "",
    city: "",
  };

  const onType = (code) => {
    const pt = types.find((t) => String(t.PCode) === String(code));
    if (pt) patch({ purchaseType: pt });
  };

  const onSup = (s) => {
    if (!s) return;
    patch({
      supplierObj: s,
      city: s.area || value.city,
      shortName: clean(s.SName) || clean(s.Custname).slice(0, 10),
      address:
        [s.add1, s.add2, s.add3, s.add4]
          .map(clean)
          .filter(Boolean)
          .join(", ") || clean(s.area),
      gstin: clean(s.TIN),
      panNo: clean(s.PAN),
      station: String(s.Station || "").toUpperCase(),
    });
  };

  const onDate = (d) => {
    const iso = d ? d.format("YYYY-MM-DDTHH:mm:ss") : null;
    patch({ invoiceDate: iso, stockRecDate: iso });
  };

  const shared = value.invoiceDate || value.stockRecDate;
  const pt = value.purchaseType;
  const sup = value.supplierObj;
  const stLbl = String(sup?.Station || "").toUpperCase();
  const stCls =
    stLbl === "OUTSTATION" ? "outstation" : stLbl === "LOCAL" ? "local" : "";

  return (
    <>
      <style>{CSS}</style>
      <div className="phf-root">
        <div className="phf-body">
          {/* ══ LEFT: Entry Details ══ */}
          <div className="phf-sec">
            <div className="phf-sec-title">
              <FileTextOutlined />
              Entry Details
            </div>
            <div className="phf-egrid">
              <EF
                label="Purchase Type"
                icon={<FileTextOutlined />}
                required
                nav={nav("ptype", "select")}
              >
                {tErr ? (
                  <span className="phf-ptype-err">{tErr}</span>
                ) : (
                  <Select
                    size="small"
                    style={{ width: "100%" }}
                    value={pt?.PCode ?? undefined}
                    ref={setRef("ptype")}
                    onDropdownVisibleChange={trkOp("ptype")}
                    onChange={onType}
                    loading={tLoad}
                    disabled={disabled}
                    placeholder="Select type"
                    popupClassName="phf-dd"
                    options={types.map((t) => ({
                      value: t.PCode,
                      label: `${t.PType} (${t.PCode})`,
                    }))}
                    status={err("ptype")}
                    bordered={false}
                  />
                )}
              </EF>

              <EF label="Entry No" icon={<NumberOutlined />}>
                <div className={`phf-eno${!value.entryNo ? " empty" : ""}`}>
                  {value.entryNo || "Auto"}
                </div>
              </EF>

              <EF label="Date" icon={<CalendarOutlined />}>
                <DatePicker
                  size="small"
                  style={{ width: "100%" }}
                  value={value.date ? dayjs(value.date) : dayjs()}
                  format="DD-MM-YYYY"
                  allowClear={false}
                  disabled={disabled}
                  onChange={(d) => patch({ date: d ? d.format("YYYY-MM-DDTHH:mm:ss") : null })}
                  bordered={false}
                />
              </EF>

              <EF
                label="Invoice No"
                icon={<FileTextOutlined />}
                required
                nav={nav("invoiceNo", "input")}
              >
                <Input
                  size="small"
                  value={value.invoiceNo || ""}
                  ref={setRef("invoiceNo")}
                  onChange={(e) => patch({ invoiceNo: e.target.value })}
                  placeholder="Invoice No"
                  disabled={disabled}
                  status={err("invoiceNo")}
                  bordered={false}
                  style={{ padding: 0 }}
                />
              </EF>

              <EF label="Invoice Date" icon={<CalendarOutlined />} required>
                <DatePicker
                  size="small"
                  style={{ width: "100%" }}
                  value={shared ? dayjs(shared) : null}
                  format="DD-MM-YYYY"
                  placeholder="Invoice Date"
                  disabled={disabled}
                  onChange={onDate}
                  status={err("invoiceDate")}
                  bordered={false}
                />
              </EF>
            </div>
          </div>

          {/* ══ RIGHT: Supplier Details ══ */}
          <div className="phf-sec">
            <div className="phf-sec-title">
              <ShopOutlined />
              Supplier Details
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {sup ? (
                /* ── selected: single flat row ── */
                <div className="phf-sp-row">
                  {/* avatar + name pill */}
                  <div className="phf-sav-wrap">
                    <div
                      className="phf-sav"
                      style={{ background: avColor(sup.Custname) }}
                    >
                      {avInit(sup.Custname)}
                    </div>
                    <span className="phf-sav-name" title={sup.Custname}>
                      {sup.Custname}
                    </span>
                  </div>

                  {/* inline KV strip — row 1: Short Name, GSTIN, PAN, Station | row 2: Address */}
                  <div className="phf-kv-strip">
                    {/* row 1 */}
                    <div className="phf-kv-row1">
                      <KV
                        label="Short Name"
                        icon={<IdcardOutlined />}
                        value={value.shortName}
                        t="grey"
                      />
                      <KV
                        label="GSTIN"
                        icon={<SafetyOutlined />}
                        value={value.gstin}
                        t="amber"
                      />
                      <KV
                        label="PAN No"
                        icon={<BankOutlined />}
                        value={value.panNo}
                        t="blue"
                      />
                      <div
                        className="phf-kv"
                        style={{
                          background: T.grey[0],
                          borderColor: T.grey[1],
                        }}
                      >
                        <div
                          className="phf-kv-lbl"
                          style={{ color: T.grey[2] }}
                        >
                          <EnvironmentOutlined />
                          Station
                        </div>
                        <span className={`phf-st ${stCls}`}>
                          {stCls === "outstation"
                            ? "Outstation"
                            : stCls === "local"
                              ? "Local"
                              : "—"}
                        </span>
                      </div>
                    </div>
                    {/* row 2 */}
                    <div className="phf-kv-row2">
                      <KV
                        label="Address"
                        icon={<HomeOutlined />}
                        value={value.address}
                        t="teal"
                        wrap
                        className="kfull"
                      />
                    </div>
                  </div>

                  {/* change / clear */}
                  <div className="phf-actions-col">
                    <button
                      className="phf-ib ch"
                      title="Change supplier"
                      onClick={() => setBrowseOpen(true)}
                      disabled={disabled}
                    >
                      <SearchOutlined />
                    </button>
                    <button
                      className="phf-ib cl"
                      title="Clear supplier"
                      onClick={() => patch(cleared)}
                      disabled={disabled}
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                </div>
              ) : (
                /* ── empty state ── */
                <div className="phf-sempty">
                  <div className="phf-sempty-icon">
                    <ShopOutlined />
                  </div>
                  <span className="phf-sempty-txt">No supplier selected</span>
                  <button
                    className="phf-browsebtn"
                    onClick={() => setBrowseOpen(true)}
                    disabled={disabled || sLoad}
                  >
                    <SearchOutlined />
                    {sLoad ? "Loading…" : "Browse Suppliers"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SupplierModal
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        suppliers={sups}
        loading={sLoad}
        onSelect={onSup}
      />
    </>
  );
};

export default PurchaseHeaderForm;
